import unescapedText from 'I18n/utils/unescapedText';
import MobilePanel from 'marketplace-ui-common/components/mobile/MobilePanel';
import MobilePanelBody from 'marketplace-ui-common/components/mobile/MobilePanelBody';
import MobileTypeaheadResults from 'marketplace-ui-common/components/mobile/MobileTypeaheadResults';
import MobileSearchBar from 'marketplace-ui-common/search/components/MobileSearchBar';
import { useStorefrontHeaderBackgroundStyles } from 'marketplace-ui-common/storefront/components/StorefrontHeader.utils';
import { debounce } from 'marketplace-ui-common/utils/debounce';
import { GLOBAL_NAVIGATION_TOOLBAR_HEIGHT } from 'nav-meta/constants';
import { useCallback, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledStickySearchBarWrapper = styled.div.withConfig({
  displayName: "MobileStorefrontSearchBar__StyledStickySearchBarWrapper",
  componentId: "sc-1ukqhn-0"
})(() => {
  const theme = useTheme();
  const backgroundStyles = useStorefrontHeaderBackgroundStyles({
    color: 'blue'
  });
  return Object.assign({}, backgroundStyles, {
    position: 'fixed',
    zIndex: 9999,
    top: 0,
    left: 0,
    width: '100%',
    height: GLOBAL_NAVIGATION_TOOLBAR_HEIGHT,
    paddingInline: theme.space['200'],
    paddingBlock: theme.space['100']
  });
});
export default function MobileStorefrontSearchBar({
  placeholder,
  searchTerm,
  onClearSearch,
  onSearchSubmit,
  onInputValueChange,
  onSelectedOptionChange,
  typeaheadResults
}) {
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);
  const [mobileSearchBarFocused, setMobileSearchBarFocused] = useState(false);
  const [mobileTypeaheadOpen, setMobileTypeaheadOpen] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [mobileSearchBarButtonFocused, setMobileSearchBarButtonFocused] = useState(false);
  const innerSearchBarRef = useRef(null);
  const outerSearchBarRef = useRef(null);
  const fetchTypeaheadResults = useCallback(searchValue => onInputValueChange(searchValue), [onInputValueChange]);
  const searchSubmit = useCallback(value => {
    setMobileTypeaheadOpen(false);
    onSearchSubmit(value);
  }, [onSearchSubmit]);
  const clickMobileSearchBar = useCallback(() => {
    setMobileSearchBarFocused(true);
    setMobileTypeaheadOpen(true);
  }, []);
  const focusMobileSearchBar = useCallback(evt => {
    setMobileSearchBarFocused(true);
    setMobileTypeaheadOpen(true);
    if (evt.target.value && !typeaheadResults.length) {
      fetchTypeaheadResults(evt.target.value);
    }
  }, [fetchTypeaheadResults, typeaheadResults.length]);
  const cancelMobileSearch = useCallback(() => {
    setMobileSearchBarFocused(false);
    setMobileTypeaheadOpen(false);
  }, []);
  const selectMobileTypeaheadItem = useCallback(item => {
    onClearSearch();
    setMobileSearchBarFocused(false);
    setMobileTypeaheadOpen(false);
    onSelectedOptionChange({
      target: {
        value: item
      }
    });
  }, [onClearSearch, onSelectedOptionChange]);
  const onSearchUpdate = useCallback(searchValue => {
    const trimmedSearchValue = searchValue.trim();
    if (!trimmedSearchValue) {
      onClearSearch();
    }
    fetchTypeaheadResults(trimmedSearchValue);
    setLocalSearchTerm(trimmedSearchValue);
  }, [fetchTypeaheadResults, onClearSearch]);
  const focusInnerSearchOnPanelOpen = useCallback(() => {
    const innerSearchBar = innerSearchBarRef.current;
    if (innerSearchBar) {
      const innerSearchInput = innerSearchBar.querySelector('.mobile-search-input');
      if (innerSearchInput) {
        innerSearchInput.focus();
        setMobileSearchBarButtonFocused(false);
      }
    }
  }, []);
  const focusOuterSearchOnPanelClose = useCallback(() => {
    setMobileSearchBarButtonFocused(true);
  }, []);
  const onPanelOpen = useCallback(() => {
    setScrollPosition(window.scrollY);
    window.onscroll = () => {
      window.scrollTo(0, 0);
    };
  }, []);
  const onPanelClose = useCallback(() => {
    window.onscroll = () => {};
    window.scrollTo(0, scrollPosition);
  }, [scrollPosition]);
  return /*#__PURE__*/_jsxs("div", {
    ref: outerSearchBarRef,
    children: [/*#__PURE__*/_jsx(MobileSearchBar, {
      ariaLabel: unescapedText('appMarketplace.storefront.searchSelect.searchInputPlaceholder'),
      buttonFocused: mobileSearchBarButtonFocused,
      cancelSearch: cancelMobileSearch,
      focused: false,
      inputValue: localSearchTerm,
      onClear: onClearSearch,
      onClick: clickMobileSearchBar,
      onSearchSubmit: searchSubmit,
      onSearchUpdate: onSearchUpdate,
      placeholder: placeholder !== null && placeholder !== void 0 ? placeholder : '',
      use: "placeholder"
    }), /*#__PURE__*/_jsx(MobilePanel, {
      onOpenStart: onPanelOpen,
      onOpenComplete: focusInnerSearchOnPanelOpen,
      onCloseStart: onPanelClose,
      onCloseComplete: focusOuterSearchOnPanelClose,
      open: mobileTypeaheadOpen,
      align: "top",
      children: /*#__PURE__*/_jsxs(MobilePanelBody, {
        ref: innerSearchBarRef,
        children: [/*#__PURE__*/_jsx(StyledStickySearchBarWrapper, {
          children: /*#__PURE__*/_jsx(MobileSearchBar, {
            cancelSearch: cancelMobileSearch,
            focused: mobileSearchBarFocused || mobileTypeaheadOpen,
            inputValue: localSearchTerm,
            onClear: onClearSearch,
            onSearchFocus: focusMobileSearchBar,
            onSearchSubmit: searchSubmit,
            onSearchUpdate: debounce(onSearchUpdate),
            placeholder: placeholder !== null && placeholder !== void 0 ? placeholder : ''
          })
        }), /*#__PURE__*/_jsx(MobileTypeaheadResults, {
          options: typeaheadResults,
          onChange: selectMobileTypeaheadItem
        })]
      })
    })]
  });
}